import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _get from 'lodash/get';

import DatePicker from 'cccisd-datepicker';

import CategoryCard from './components/CategoryCard';
import { noYouthWarning, switchPlanNameIfNeeded } from '../../Administer/helpers';

import {
    setSelectedYouth,
    setStartDateFilter,
    setEndDateFilter,
    setPlanNameFilter,
} from '../../../reducers/dashboard/dashboardSlice';
import { downloadCSV } from '../helpers';
import ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES from '../../Administer/ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES.json';

import style from './style.css';

const IndividualDataTab = () => {
    // state
    const dispatch = useDispatch();
    const allAnswerData = useSelector(state => {
        return state.app.dashboard.allAnswerData;
    });
    const selectedYouth = useSelector(state => {
        return state.app.dashboard.selectedYouth;
    });
    const youthList = useSelector(state => {
        return state.app.careCoordinator.youthList;
    });
    const aggregateScoreAverages = useSelector(state => state.app.dashboard.aggregateScoreAverages);

    // filters
    const startDateFilterValue = useSelector(state => {
        return state.app.dashboard.filters.startDate;
    });
    const endDateFilterValue = useSelector(state => {
        return state.app.dashboard.filters.endDate;
    });
    const planNameFilterValue = useSelector(state => {
        return state.app.dashboard.filters.planName;
    });

    const filters = {
        startDate: startDateFilterValue,
        endDate: endDateFilterValue,
        planName: planNameFilterValue,
    };

    const DOES_NOT_RECEIVE_MESSAGES_COLOR = '#ff000052';
    const RECEIVE_MESSAGES_COLOR = 'yellowgreen';

    if (youthList.length === 0) {
        return noYouthWarning({
            message: (
                <div>
                    Welcome to SMART-Wrap! To get started, please navigate to the
                    <a href="/manage" style={{ color: '#4a2e84' }}>
                        {' '}
                        <u>Manage</u>{' '}
                    </a>
                    tab above to add your families.
                </div>
            ),
            showIcon: false,
        });
    }

    return (
        <div>
            <div className={style.main_container}>
                <div className={style.selector_and_info_container}>
                    <div style={{ width: '40%' }}>
                        <label className="form-label">Youth: </label>
                        <select
                            className="form-select"
                            onChange={e => {
                                const newValue = e.target.value;
                                const selected = youthList.find(youth => youth.pawn.pawnId === Number(newValue));

                                dispatch(setSelectedYouth(selected));
                            }}
                            value={selectedYouth ? selectedYouth.pawn.pawnId : ''}
                        >
                            <option key={-1} value="">
                                --- Select a youth ---
                            </option>
                            {youthList.map((youth, i) => {
                                return (
                                    <option
                                        value={youth.pawn.pawnId}
                                        key={i}
                                    >{`${youth.fields.firstName} ${youth.fields.lastName}`}</option>
                                );
                            })}
                        </select>
                        {selectedYouth && (
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    style={{
                                        height: 'fit-content',
                                        marginTop: '25px',
                                        marginBottom: '25px',
                                    }}
                                    onClick={() => {
                                        downloadCSV(
                                            aggregateScoreAverages,
                                            allAnswerData,
                                            `${selectedYouth.fields.wrapId}-individual_data.csv`
                                        );
                                    }}
                                >
                                    Download CSV
                                </button>
                                <div>
                                    <br />
                                    <div className={style.tooltip_text}>
                                        Note: Youth and caregiver averages are based on a range of 0-3. Higher scores
                                        indicate more positive responses, while lower scores can identify areas of need.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {selectedYouth && (
                        <div className={style.individual_youth_info_container}>
                            <div className={style.participant}>
                                <div className={style.youth_section}>
                                    <h3>
                                        {selectedYouth.fields.firstName}{' '}
                                        <span className={style.username}>({selectedYouth.fields.wrapId})</span>
                                    </h3>
                                    <div className={style.detail}>
                                        Receiving Messages:{' '}
                                        <span
                                            style={{
                                                backgroundColor: selectedYouth.fields.receiveMessages
                                                    ? RECEIVE_MESSAGES_COLOR
                                                    : DOES_NOT_RECEIVE_MESSAGES_COLOR,
                                                borderRadius: '5px',
                                                padding: '2px',
                                            }}
                                        >
                                            {selectedYouth.fields.receiveMessages ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                </div>

                                {_get(selectedYouth, 'parentRoles.caregiverList', []).map((caregiverInfo, i) => {
                                    return (
                                        <div className={style.caregivers_section} key={i}>
                                            <div className={style.caregiver}>
                                                <span className={style.name}>
                                                    {`${caregiverInfo.fields.firstName} ${caregiverInfo.fields.lastName}`}{' '}
                                                    <span className={style.username}>
                                                        ({caregiverInfo.fields.wrapId})
                                                    </span>
                                                </span>
                                                <div className={style.detail}>
                                                    Receiving Messages:{' '}
                                                    <span
                                                        style={{
                                                            backgroundColor: caregiverInfo.fields.receiveMessages
                                                                ? RECEIVE_MESSAGES_COLOR
                                                                : DOES_NOT_RECEIVE_MESSAGES_COLOR,
                                                            borderRadius: '5px',
                                                            padding: '2px',
                                                        }}
                                                    >
                                                        {caregiverInfo.fields.receiveMessages ? 'Yes' : 'No'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
                {selectedYouth && (
                    <div>
                        <div style={{ marginBottom: '25px' }} />

                        <div className={style.dividing_line} style={{ marginTop: '-15px', marginBottom: '10px' }} />

                        <div className={style.filters_container}>
                            <div className={style.filters_flex_row}>
                                <div>
                                    <label className="form-label">Date Range: </label>{' '}
                                    {(filters.startDate || filters.endDate) && (
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-xs"
                                            onClick={() => {
                                                dispatch(setStartDateFilter(''));
                                                dispatch(setEndDateFilter(''));
                                            }}
                                        >
                                            clear
                                        </button>
                                    )}
                                    <br />
                                    <div className={style.date_range_container}>
                                        <div>
                                            <div>Start: </div>
                                            <DatePicker
                                                selected={filters.startDate}
                                                onChange={e => {
                                                    const newValue = e;

                                                    dispatch(setStartDateFilter(newValue));
                                                }}
                                                placeholderText="mm/dd/yyyy"
                                            />
                                        </div>
                                        <div>
                                            <div>End: </div>
                                            <DatePicker
                                                selected={filters.endDate}
                                                onChange={e => {
                                                    const newValue =
                                                        e === null
                                                            ? ''
                                                            : new Date(new Date(e).setHours(23, 59, 59, 999));

                                                    dispatch(setEndDateFilter(newValue));
                                                }}
                                                placeholderText="mm/dd/yyyy"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="form-label">Plan Name: </label>
                                    <select
                                        className="form-select"
                                        onChange={e => {
                                            const newValue = e.target.value;

                                            dispatch(setPlanNameFilter(newValue));
                                        }}
                                        value={filters.planName}
                                    >
                                        <option value="" />
                                        {Object.keys(ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES).map((planName, i) => {
                                            return (
                                                <option key={i} value={planName.toLowerCase()}>
                                                    {switchPlanNameIfNeeded(planName)}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className={style.individual_data_cards_container}>
                            <CategoryCard
                                youth={selectedYouth}
                                categoryName="Theraputic Alliance"
                                categoryHandle="alliance"
                                filters={filters}
                            />
                            <CategoryCard
                                youth={selectedYouth}
                                categoryName="Wraparound Fidelity"
                                categoryHandle="fidelity"
                                filters={filters}
                            />
                            <CategoryCard
                                youth={selectedYouth}
                                categoryName="Satisfaction with Care"
                                categoryHandle="satisfaction"
                                filters={filters}
                            />
                            <CategoryCard
                                youth={selectedYouth}
                                categoryName="Making Progress"
                                categoryHandle="progress"
                                filters={filters}
                            />
                            <CategoryCard
                                youth={selectedYouth}
                                categoryName="Caregiver and Family Outcomes"
                                categoryHandle="outcomesCaregiver"
                                filters={filters}
                            />
                            <CategoryCard
                                youth={selectedYouth}
                                categoryName="Youth Outcomes"
                                categoryHandle="outcomes"
                                filters={filters}
                            />
                            <CategoryCard
                                youth={selectedYouth}
                                categoryName="Comments"
                                isCommentsCategory
                                categoryHandle="feedback"
                                filters={filters}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IndividualDataTab;
